import { useState } from "react";
import { PenLine, Tablet, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const openStudioSigning = async (cid) => {
  const { data } = await api.post(`/contracts/${cid}/studio-view`);
  window.location.assign(data.url);
};

const Mode = ({ value, cur, set, icon: I, title, text }) => (
  <button type="button" onClick={() => set(value)} data-testid={`sign-mode-${value}`}
    className={`text-left p-3 rounded-md border transition-colors ${cur === value ? "border-[#0A2540] bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
    <p className="font-medium text-sm text-slate-900 flex items-center gap-2"><I className="h-4 w-4" />{title}</p>
    <p className="text-xs text-slate-500 mt-1">{text}</p>
  </button>
);

export function SendForSignature({ c, prospect, disabled, onDone }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("email");
  const [checked, setChecked] = useState(false);
  const [doc, setDoc] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const start = () => {
    setDoc([prospect?.id_doc_type, prospect?.id_doc_number].filter(Boolean).join(" n. "));
    setChecked(false); setMode("email"); setOpen(true);
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/contracts/${c.id}/sign`, { mode, id_checked: checked, id_doc: doc, message });
      if (mode === "studio") { toast.success("Apertura della firma sul tablet…"); await openStudioSigning(c.id); return; }
      toast.success("Contratto inviato: DocuSign ha mandato l'email al cliente");
      setOpen(false); onDone();
    } catch (e) { toast.error(errMsg(e)); onDone(); } finally { setBusy(false); }
  };

  return (
    <>
      <Button disabled={disabled} onClick={start} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="contract-sign"><PenLine className="h-4 w-4 mr-1" />Invia in firma</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="bg-white max-w-lg" data-testid="sign-dialog">
          <DialogHeader><DialogTitle className="font-heading">Invia in firma</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <Mode value="email" cur={mode} set={setMode} icon={Mail} title="Firma via email" text={`DocuSign scrive a ${prospect?.email || "—"}`} />
            <Mode value="studio" cur={mode} set={setMode} icon={Tablet} title="Firma in studio sul tablet" text="Il cliente firma subito qui, poi Bruno riceve l'email" />
          </div>
          {mode === "email" ? (
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Messaggio per il cliente (facoltativo)" maxLength={1000} data-testid="sign-message" />
          ) : (
            <div className="space-y-3 text-sm">
              <Input value={doc} onChange={(e) => setDoc(e.target.value)} placeholder="Documento controllato (tipo e numero)" data-testid="sign-id-doc" />
              <label className="flex items-start gap-2 text-slate-700">
                <Checkbox checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid="sign-id-checked" className="mt-0.5" />
                Ho verificato di persona il documento d'identità del cliente. La conferma viene registrata con il mio nome e l'ora.
              </label>
            </div>
          )}
          <p className="text-xs text-slate-500">Dopo l'invio il contratto non è più modificabile. Firmerà poi Bruno Benvenuti per convalida, via email.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="sign-cancel">Annulla</Button>
            <Button disabled={busy || (mode === "studio" && (!checked || !doc.trim()))} onClick={submit} className="bg-[#0A2540] hover:bg-[#031B33] text-white" data-testid="sign-submit">
              {mode === "studio" ? "Avvia firma sul tablet" : "Invia email di firma"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
